import { WHATS_NEW_ROUTE } from '../changelog/changelog';
import { t, type StringKey } from '../strings';
import { recordKindLine } from './listPages';
import { documentName, isNamedDocumentKind } from '../documents/documentNames';
import type { BannerPill, PagedRecord } from './recordPages';

/**
 * 🔒 The one line under the name, for every page in the application (T176). The
 * product owner, 2026-09-22: *"How many custom one off items are there that
 * should be a function like page title is this a function that is used over or
 * custom for each page"*, and *"i would like to know more on the bypass of
 * standard function why are the cases not built in the function"*.
 *
 * 🔨 The cases are built in here rather than bypassed per page. Eight document
 * pages each typed their own line, the record archetype typed a second, the
 * settings shell a third and four section pages a fourth; a page that types its
 * own line is a page that can drift from the standard without anything noticing.
 *
 * 🔒 The title bar standard carries exactly one line under the name (decision
 * 0621), and this function is what that line is.
 */

/** A subject whose line is the record type's own name, or its classification pills where it has them. */
export interface RecordKindLineSubject {
  page: 'record';
  recordType: PagedRecord;
  /**
   * 🔒 The classification pills a Company and a Person draw instead of the type's
   * name (T175). The server computes them, because only the server knows a
   * person's employments and which company is ours.
   */
  classifications?: readonly BannerPill[] | null;
  /**
   * 🔒 The record the kind line names after the type, as a link (decision 0722):
   * a Deal's customer, so the line reads *Deal · Ashcroft Industrial*. Any
   * classification pills follow it rather than standing in its place.
   */
  parent?: KindLineParent | null;
  /**
   * 🔒 An Item's own part number, which the line reads in place of the type's name
   * (decision 2154, amending 0621); an Item with none reads *Item*.
   */
  partNumber?: string | null;
}

/**
 * The record a kind line names after its type, and where it links to. 🔒 A null route
 * says this viewer cannot open the record, and the name then stands as plain text
 * (decision 2153).
 */
export interface KindLineParent {
  text: string;
  route: string | null;
}

/** A subject whose line is the document kind's name, in Title Case. */
export interface DocumentKindLineSubject {
  page: 'document';
  documentType: string;
}

/** A settings page, whose line is the breadcrumb through its category. */
export interface SettingsKindLineSubject {
  page: 'settings';
  category: string;
}

/** A named page that is neither a record nor a document nor settings: its line is registered below. */
export interface NamedPageKindLineSubject {
  page: 'named';
  route: string;
}

export type PageKindLineSubject = RecordKindLineSubject | DocumentKindLineSubject | SettingsKindLineSubject | NamedPageKindLineSubject;

/**
 * What the slot draws: a line of text, the classification pills that stand in its
 * place, or the type's name with the record it belongs to as a link and its pills
 * after it (decision 0722).
 */
export type PageKindLine =
  | { kind: 'text'; text: string }
  | { kind: 'pills'; pills: readonly BannerPill[] }
  | { kind: 'linked'; text: string; parent: KindLineParent; pills: readonly BannerPill[] };

export class PageKindLineError extends Error {}

/**
 * 🔒 The pages whose line is neither a record type, a document kind nor a
 * settings breadcrumb, keyed on the route each is reached at (T176). A page's
 * line is registered here rather than typed at the page, so the standard has one
 * place to read and one place to change.
 */
export const NAMED_PAGE_KIND_LINES: Readonly<Record<string, StringKey>> = {
  '/todos': 'todos.subtitle',
  '/org': 'org.subtitle',
  '/org/exceptions': 'org.exceptionsSubtitle',
  '/locations/review': 'review.subtitle',
  // Decision 5382: Records Needing Data Review, the page `queue:data-quality` names.
  '/data-quality': 'recordChecks.review.subtitle',
  // Piece 28: the asset register and the won Deal's conversion screen.
  '/assets': 'asset.subtitle',
  '/assets/convert': 'asset.convert.subtitle',
  // Decision 1293: the asset template library.
  '/assets/templates': 'asset.template.subtitle',
  // Piece 30: the knowledge base's list, an article, the category tree, a quiz and the two reports.
  '/kb': 'kb.subtitle',
  '/kb/:id': 'kb.article.kindLine',
  '/kb/manage': 'kb.manage.subtitle',
  '/kb/checklists': 'kb.checklists.subtitle',
  '/kb/:id/quiz': 'kb.quiz.kindLine',
  '/kb/:id/quiz/edit': 'kb.quiz.editKindLine',
  '/kb/:id/quiz/report': 'kb.report.kindLine',
  '/kb/people/:personId': 'kb.personReport.kindLine',
  // Piece 32: the mail client and its quarantine review.
  '/mail': 'mail.subtitle',
  '/mail/quarantine': 'mail.quarantine.subtitle',
  '/mail/mailboxes/:mailboxId': 'mail.record.subtitle',
  // T419, decision 1235: the Mail Settings window, on its first section and on any one it names.
  '/mail/settings': 'mail.settingsWindow.kindLine',
  '/mail/settings/:section': 'mail.settingsWindow.kindLine',
  // Piece 34: the HR module's pages.
  '/hr/employees': 'hr.employees.subtitle',
  // Decision 3180: one job's employee file, and the employee's own copy from the user menu.
  '/hr/employees/:employmentId': 'hr.file.subtitle',
  // Decision 10651: one job's unemployment claims, under its Employee page.
  '/hr/employees/:employmentId/unemployment-claims': 'hr.claims.subtitle',
  '/my-info': 'myInfo.subtitle',
  '/hr/certifications': 'hr.cert.subtitle',
  '/hr/complaints': 'hr.complaint.subtitle',
  // Decision 11860: the phone system's three pages and one conversation's thread.
  '/phone/calls': 'phonePages.calls.subtitle',
  '/phone/voicemail': 'phonePages.voicemail.subtitle',
  '/phone/texts': 'phonePages.texts.subtitle',
  '/phone/texts/:number': 'phonePages.texts.threadSubtitle',
  // Piece 34's time queue, built with Submit Day (decision 1983).
  '/time/approve': 'timeApproval.subtitle',
  // Decision 13250: the approver's time-off requests alone, from the HR group's Time Off Requests row.
  '/hr/time-off-requests': 'hr.timeOffApproval.subtitle',
  [WHATS_NEW_ROUTE]: 'whatsNew.subtitle',
};

/**
 * 🔒 The kind line a Company and a Person draw in place of their record type's
 * name (T175). The product owner, 2026-09-22: *"the company (customer, vendor,
 * manufacture, Undefined) and on person (Employee, Customer, Vendor,
 * Manufacture, Retired, Undefined) pill in place of person or company under the
 * name. if there are no flags to what the item is it is undefined"*.
 *
 * 🔨 Several at once is ordinary, not an error: a company we buy from and sell to
 * draws both, and a person employed by two companies draws what each of them is.
 */
export const CLASSIFICATION_UNDEFINED = 'Undefined';
export const CLASSIFICATION_RETIRED = 'Retired';
export const CLASSIFICATION_EMPLOYEE = 'Employee';

/** The three flags a company carries, which are what it is to us. */
export interface CompanyClassificationFlags {
  isCustomer?: boolean | null;
  isVendor?: boolean | null;
  isManufacturer?: boolean | null;
}

/** One employment as the pills read it: whether it has ended, whether it is at our own company, and what its employer is to us. */
export interface EmploymentClassification extends CompanyClassificationFlags {
  ended: boolean;
  isOwnCompany?: boolean | null;
}

function flagPills(flags: CompanyClassificationFlags): BannerPill[] {
  const pills: BannerPill[] = [];
  if (flags.isCustomer === true) pills.push({ text: 'Customer', group: 'good' });
  if (flags.isVendor === true) pills.push({ text: 'Vendor', group: 'good' });
  if (flags.isManufacturer === true) pills.push({ text: 'Manufacturer', group: 'good' });
  return pills;
}

/** 🔨 A company with no flag at all is Undefined and never an empty line: an empty line says the page failed to load something. */
export function companyClassificationPills(flags: CompanyClassificationFlags): BannerPill[] {
  const pills = flagPills(flags);
  return pills.length > 0 ? pills : [{ text: CLASSIFICATION_UNDEFINED, group: 'inactive' }];
}

/**
 * 🔒 A person's classification is their current employments' (T175): Employee at
 * our own company, and what each other current employer is to us.
 *
 * 🔨 Retired is the case where every employment has ended and there was at least
 * one, and Undefined is the case where there is none. The two are not the same
 * answer: one says the relationship finished and the other says nobody recorded
 * one, and a person the office retired reading as Undefined is a record somebody
 * would go and "fix".
 */
export function personClassificationPills(employments: readonly EmploymentClassification[]): BannerPill[] {
  if (employments.length === 0) return [{ text: CLASSIFICATION_UNDEFINED, group: 'inactive' }];
  const current = employments.filter((row) => !row.ended);
  if (current.length === 0) return [{ text: CLASSIFICATION_RETIRED, group: 'inactive' }];
  const pills: BannerPill[] = [];
  if (current.some((row) => row.isOwnCompany === true)) pills.push({ text: CLASSIFICATION_EMPLOYEE, group: 'good' });
  /* 🔨 Each classification appears once however many current employers carry it: two customer employers are one Customer pill. */
  for (const pill of current.flatMap((row) => flagPills(row))) {
    if (!pills.some((held) => held.text === pill.text)) pills.push(pill);
  }
  return pills.length > 0 ? pills : [{ text: CLASSIFICATION_UNDEFINED, group: 'inactive' }];
}

/**
 * 🔒 The document kind's own name for the line under its number (T176), Title
 * Case, from the one map every document name is read from (decision 0638). A
 * type nothing names is refused rather than drawn as its own identifier: a
 * banner reading `CreditNote` is a banner nobody wrote.
 */
export function documentKindLine(documentType: string): string {
  if (!isNamedDocumentKind(documentType)) throw new PageKindLineError(`no kind line is registered for the document type "${documentType}"`);
  return documentName(documentType);
}

/** 🔒 The settings breadcrumb, which is the line every settings page draws (T176). */
export function settingsKindLine(category: string): string {
  return `${t('settings.title')} › ${category}`;
}

export function pageKindLine(subject: PageKindLineSubject): PageKindLine {
  switch (subject.page) {
    case 'record': {
      const pills = subject.classifications ?? null;
      if (subject.parent) return { kind: 'linked', text: recordKindLine(subject.recordType), parent: subject.parent, pills: pills ?? [] };
      if (pills !== null) return { kind: 'pills', pills };
      const part = subject.recordType === 'Item' ? subject.partNumber?.trim() : '';
      return { kind: 'text', text: part ? part : recordKindLine(subject.recordType) };
    }
    case 'document':
      return { kind: 'text', text: documentKindLine(subject.documentType) };
    case 'settings':
      return { kind: 'text', text: settingsKindLine(subject.category) };
    case 'named': {
      const key = NAMED_PAGE_KIND_LINES[subject.route];
      if (key === undefined) throw new PageKindLineError(`no kind line is registered for the page at "${subject.route}"`);
      return { kind: 'text', text: t(key) };
    }
  }
}

/** The line as one string, for a caller whose slot takes no pills — a page title, a test, a document heading. */
export function pageKindLineText(subject: PageKindLineSubject): string {
  const line = pageKindLine(subject);
  if (line.kind === 'linked') return [line.text, line.parent.text, ...line.pills.map((pill) => pill.text)].join(' · ');
  return line.kind === 'text' ? line.text : line.pills.map((pill) => pill.text).join(' · ');
}

/** 🔒 Every document kind whose page draws a kind line, which is what the shared test reads (T176). */
export const DOCUMENT_KIND_LINE_TYPES: readonly string[] = [
  'Quote', 'SalesOrder', 'ChangeOrder', 'PurchaseOrder', 'Invoice', 'CreditNote', 'Payment', 'Bill', 'RFQ', 'WorkOrder', 'VendorCredit',
];
